import { useState, useEffect, useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { usePermissions } from "@/hooks/use-permissions";
import { useListFilters } from "@/hooks/use-list-filters";
import { useFacilities, type FacilityFilters as FacilityListParams } from "@/hooks/use-facilities";
import {
  FacilityFilters,
  defaultFacilityFilters,
  type FacilityFilterState,
} from "@/components/facilities/facility-filters";
import { useFacilityColumns } from "@/components/facilities/facility-columns";
import { FacilityRowMenu, useFacilityRowMenu } from "@/components/facilities/facility-row-menu";
import { LetterCodeDialog } from "@/components/facilities/letter-code-dialog";
import { useLetterCodeDialog } from "@/components/facilities/use-letter-code-dialog";
import { CreateViolationDrawer } from "@/components/violations/create-violation-drawer";
import { RegistryLoadError } from "@/components/custom-fields/registry-load-error";
import { BulkEditEntryButton } from "@/components/bulk-edits/entry/bulk-edit-entry-button";
import { facilityListSelection } from "@/components/bulk-edits/entry/facility-list-selection";
import { useSettledSelectionTotal } from "@/components/bulk-edits/entry/use-settled-selection-total";
import {
  PageHeader,
  Button,
  DataTable,
  Pagination,
  EmptyState,
  SkeletonTable,
  sortByForColumnId,
  visibleColumnCount as computeVisibleColumnCount,
} from "@/components/ui";
import { useListColumnPreferences } from "@/hooks/use-list-column-preferences";
import { useEntityColumnRegistry } from "@/hooks/use-entity-column-registry";
import { FACILITY_COLUMN_REGISTRY } from "@/components/facilities/facility-column-registry";

type FacilityListItem = components["schemas"]["FacilityListItem"];

export const Route = createFileRoute("/_authenticated/facilities/")({
  component: FacilitiesListPage,
});

const PAGE_SIZE = 20;

function FacilitiesListPage() {
  const { t } = useTranslation(["facilities", "org", "common"]);
  const orgId = useAppStore((s) => s.activeOrgId);
  const { can } = usePermissions();
  const canCreate = can("facilities", "create");

  const navigate = useNavigate();
  const rowMenu = useFacilityRowMenu();
  const letterCodeDialog = useLetterCodeDialog();
  // Per-org persisted filter state — survives reloads and org switches via
  // localStorage["njbsoft-facility-filters:<orgId>"].
  const { filters, setFilter, clearFilter, resetFilters } = useListFilters<FacilityFilterState>({
    storageKey: "njbsoft-facility-filters",
    orgId,
    version: 1,
    defaults: defaultFacilityFilters,
  });
  const [offset, setOffset] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([{ id: "created_at", desc: true }]);
  // The org's custom-field definitions append as dynamic columns; the query's
  // success gates the table below (a pending/failed definitions query must
  // never reach the preference-save path).
  const {
    registry: facilityRegistry,
    defsByFieldKey,
    ready: registryReady,
    isError: registryFailed,
    refetch: refetchDefinitions,
  } = useEntityColumnRegistry("facility", FACILITY_COLUMN_REGISTRY);

  const firstSort = sorting[0];
  // Read off the DYNAMIC registry: a custom column carries sortBy null, so a
  // click on its header can never produce a wire sort key.
  const sortBy = firstSort ? sortByForColumnId(facilityRegistry, firstSort.id) : undefined;
  const sortDir = firstSort ? (firstSort.desc ? "desc" : "asc") : undefined;

  const { visibility, setColumnVisibility, resetColumns } = useListColumnPreferences(
    "facilities",
    facilityRegistry,
  );

  // Any filter or sort change resets pagination to the first page.
  useEffect(() => {
    setOffset(0);
  }, [sorting, filters]);

  // The one params object feeds both the list read and the bulk edit
  // selection, so the button always describes the rows on screen.
  const listParams: FacilityListParams = {
    ...filters,
    sort_by: sortBy,
    sort_dir: sortDir,
    limit: PAGE_SIZE,
    offset,
  };
  const { data, isLoading, isError, isPlaceholderData, error } = useFacilities(orgId, listParams);
  const selection = facilityListSelection(listParams);
  const selectionTotal = useSettledSelectionTotal(selection, {
    total: data?.total ?? 0,
    isPlaceholderData,
    isError,
  });

  // Memoized: the handler is a columns-memo dependency, so a fresh function per
  // render would rebuild every column on every render. Each row carries its own
  // derived compliance status and out-of-compliance count, so the Compliance
  // cell needs no second request — only this drill into the devices tab.
  const onDrillDevices = useCallback(
    (facilityId: string) =>
      void navigate({
        to: "/facilities/$facilityId",
        params: { facilityId },
        search: { tab: "devices" },
      }),
    [navigate],
  );
  const columns = useFacilityColumns(facilityRegistry, defsByFieldKey, onDrillDevices);
  const extraColumnCount = columns.length - facilityRegistry.length;
  const visibleColumnCount = computeVisibleColumnCount(
    facilityRegistry,
    visibility,
    extraColumnCount,
  );

  if (!orgId) {
    return (
      <div className="p-6">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  const facilities = data?.data ?? [];
  const total = data?.total ?? 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleRowClick = (row: FacilityListItem) => {
    void navigate({
      to: "/facilities/$facilityId",
      params: { facilityId: row.id },
    });
  };

  const handlePageChange = (page: number) => {
    setOffset((page - 1) * PAGE_SIZE);
  };

  if (!registryReady) {
    // Definitions unresolved: no table, no filter bar (it carries the Columns
    // picker), no preference-save path. Pending gets the standing skeleton; a
    // FAILED read gets the error and a retry, because a skeleton that never
    // resolves reads as a page still loading. The count subtitle waits for the
    // list read rather than claiming a total of zero.
    return (
      <div className="space-y-4 p-6">
        <PageHeader
          title={t("title")}
          subtitle={data ? t("totalCount", { count: total }) : undefined}
        />
        {registryFailed ? (
          <RegistryLoadError onRetry={refetchDefinitions} />
        ) : (
          <SkeletonTable columns={visibleColumnCount} rows={5} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title={t("title")}
        subtitle={t("totalCount", { count: total })}
        actions={
          <>
            <BulkEditEntryButton
              recordType="facilities"
              translation={selection}
              total={selectionTotal}
            />
            {canCreate && (
              <Button onClick={() => void navigate({ to: "/facilities/new" })}>
                {t("newFacility")}
              </Button>
            )}
          </>
        }
      />

      <FacilityFilters
        filters={filters}
        registry={facilityRegistry}
        setFilter={setFilter}
        clearFilter={clearFilter}
        resetFilters={resetFilters}
        columnVisibility={visibility}
        onColumnVisibilityChange={setColumnVisibility}
        onColumnReset={resetColumns}
      />

      {isError && (
        <div className="rounded-md bg-danger/10 p-4 text-danger">
          {t("loadFailed")}:{" "}
          {error instanceof Error ? error.message : t("unknownError", { ns: "common" })}
        </div>
      )}

      {isLoading ? (
        <SkeletonTable columns={visibleColumnCount} rows={5} />
      ) : facilities.length === 0 ? (
        <EmptyState
          icon={
            <svg
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21a.75.75 0 0 1 .75.75V21"
              />
            </svg>
          }
          title={t("empty")}
          description={t("emptyDescription")}
          actionLabel={canCreate ? t("newFacility") : undefined}
          onAction={canCreate ? () => void navigate({ to: "/facilities/new" }) : undefined}
        />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={facilities}
            onRowClick={handleRowClick}
            sorting={sorting}
            onSortingChange={setSorting}
            columnVisibility={visibility}
            onColumnVisibilityChange={setColumnVisibility}
            onRowContextMenu={rowMenu.handleRowContextMenu}
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={handlePageChange}
          />
        </>
      )}

      <FacilityRowMenu
        open={rowMenu.menuRow !== null}
        anchorRef={rowMenu.anchorRef}
        anchorPoint={rowMenu.anchorPoint}
        onClose={rowMenu.closeMenu}
        row={rowMenu.menuRow}
        onGenerateLetterCode={(row) =>
          letterCodeDialog.open({ facilityId: row.id, facilityName: row.name })
        }
        onOpenDrawer={rowMenu.openDrawer}
      />
      <LetterCodeDialog subject={letterCodeDialog.subject} onClose={letterCodeDialog.close} />
      {rowMenu.drawer?.kind === "violation" ? (
        <CreateViolationDrawer
          open
          lockedFacilityId={rowMenu.drawer.facilityId}
          onClose={rowMenu.closeDrawer}
        />
      ) : null}
    </div>
  );
}
